/**
 * Utility functions for working with GraphQL types
 * These help safely handle Maybe<T> types and nullable fields
 */

import type { Maybe } from "@/src/gql/graphql";

/**
 * Unwrap a Maybe<T> type, returning undefined if null
 */
export function unwrap<T>(value: Maybe<T>): T | undefined {
  return value ?? undefined;
}

/**
 * Unwrap a Maybe<T> type, returning a default value if null
 */
export function unwrapOrDefault<T>(value: Maybe<T>, defaultValue: T): T {
  return value ?? defaultValue;
}

/**
 * Unwrap a Maybe<string> and ensure it's a string (returns empty string if null)
 */
export function unwrapString(value: Maybe<string>): string {
  return value ?? "";
}

/**
 * Unwrap a Maybe<number> and ensure it's a number (returns 0 if null)
 */
export function unwrapNumber(value: Maybe<number>): number {
  return value ?? 0;
}

/**
 * Unwrap a Maybe<boolean> and ensure it's a boolean (returns false if null)
 */
export function unwrapBoolean(value: Maybe<boolean>): boolean {
  return value ?? false;
}

/**
 * Filter out null/undefined values from an array
 */
export function filterNulls<T>(arr: Array<Maybe<T>>): T[] {
  return arr.filter((item): item is T => item != null);
}

/**
 * Unwrap an array of Maybe<T> values.
 *
 * Accepts `undefined` as well as `null`: codegen types a nullable array column
 * as `?: Maybe<Array<Maybe<T>>>`, so the property itself can be absent, and a
 * caller reading one off a row hands us `… | undefined`.
 */
export function unwrapArray<T>(arr: Maybe<Array<Maybe<T>>> | undefined): T[] {
  if (!arr) return [];
  return filterNulls(arr);
}

/**
 * Check if a value is not null/undefined
 */
export function isNotNull<T>(value: Maybe<T>): value is T {
  return value != null;
}
